import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import type { SelectOptionItem } from "@/lib/models/ui";

import { GroupCard } from "../components/GroupCard";
import {
  UngroupedPanel,
  type UngroupedMoveTarget,
} from "../components/UngroupedPanel";
import type { BlockingError } from "../state/selectors";
import { getDuplicateCustomerAssignments } from "../state/selectors";
import type {
  CustomerMinimal,
  FileLocationRef,
  Group,
  WizardData,
} from "../state/types";

// Sentinel value used in the per-file "Move →" picker to mean "move
// this file back to the ungrouped panel". Group ids are uuids so this
// can never collide with a real group id.
const UNGROUPED_TARGET_VALUE = "__ungrouped__";

type PhaseManualGroupingProps = {
  data: WizardData;
  customers: CustomerMinimal[];
  blocking_errors: BlockingError[];

  onSelectCustomer: (groupId: string, customerId: string | null) => void;
  onMoveFile: (
    fileId: string,
    source: FileLocationRef,
    target: FileLocationRef,
  ) => void;
  onCreateManualGroup: (fileIds: string[]) => void;
  onDissolveGroup: (groupId: string) => void;
  onContinue: () => void;
};

function describeBlockingError(error: BlockingError): string {
  switch (error.kind) {
    case "no_period":
      return "Choose a period.";
    case "no_files":
      return "Select at least one PDF.";
    case "suggested_group_unresolved":
      return "A suggested group still needs Approve or Needs-manual-edit.";
    case "ungrouped_files_remain":
      return `${error.count} file${
        error.count === 1 ? " is" : "s are"
      } still ungrouped.`;
    case "group_missing_customer":
      return "A non-empty group has no selected customer.";
    case "duplicate_customer_assignment":
      return `Customer is assigned to ${error.group_ids.length} groups.`;
    case "empty_group_included":
      return "An empty group is present in state.";
    case "file_in_multiple_locations":
      return "A file appears in more than one group.";
    case "file_missing_from_all_locations":
      return "A file is missing from every group and ungrouped.";
    default: {
      const _exhaustive: never = error;
      void _exhaustive;
      return "Unknown blocking error.";
    }
  }
}

function labelForGroup(
  group: Group,
  customerById: Map<string, CustomerMinimal>,
): string {
  if (group.selected_customer_id === null) return "(no customer selected)";
  const customer = customerById.get(group.selected_customer_id);
  return customer ? customer.long_name : "(unknown customer)";
}

/**
 * Phase 3 — Manual grouping.
 *
 * Compose order from top to bottom:
 *   1. Ungrouped panel (operator's main work surface; continue is
 *      blocked until this is empty)
 *   2. Editable groups (needs_edit + manual). Each file in each group
 *      gets a per-file "Move →" picker whose targets are "Ungrouped"
 *      and every other editable group (approved groups are never
 *      valid move targets).
 *   3. Approved groups, summarised read-only.
 *
 * Selection state for the ungrouped panel is owned here so it can be
 * invalidated when files leave the panel by any path.
 */
export function PhaseManualGrouping({
  data,
  customers,
  blocking_errors,
  onSelectCustomer,
  onMoveFile,
  onCreateManualGroup,
  onDissolveGroup,
  onContinue,
}: PhaseManualGroupingProps) {
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);

  const customerById = useMemo((): Map<string, CustomerMinimal> => {
    const map = new Map<string, CustomerMinimal>();
    for (const c of customers) map.set(c.id, c);
    return map;
  }, [customers]);

  const duplicateGroupIds = useMemo((): Set<string> => {
    const out = new Set<string>();
    for (const dup of getDuplicateCustomerAssignments(data)) {
      for (const gid of dup.group_ids) out.add(gid);
    }
    return out;
  }, [data]);

  const editableGroups = useMemo(
    (): Group[] =>
      data.groups.filter(
        (g: Group) => g.state === "needs_edit" || g.state === "manual",
      ),
    [data.groups],
  );

  const approvedGroups = useMemo(
    (): Group[] => data.groups.filter((g: Group) => g.state === "approved"),
    [data.groups],
  );

  // Targets for the UngroupedPanel's bulk and single-file move
  // pickers. Approved groups are intentionally excluded.
  const ungroupedMoveTargets = useMemo(
    (): UngroupedMoveTarget[] =>
      editableGroups.map((g: Group) => ({
        group_id: g.id,
        label: labelForGroup(g, customerById),
      })),
    [editableGroups, customerById],
  );

  // Filter the ungrouped selection down to ids that are still actually
  // ungrouped. After any successful action that removes files from
  // ungrouped we also prune the selection eagerly, but this is the
  // safety net.
  const sanitizedSelectedFileIds = useMemo((): string[] => {
    const ungroupedSet = new Set<string>(data.ungrouped_file_ids);
    return selectedFileIds.filter((id: string) => ungroupedSet.has(id));
  }, [selectedFileIds, data.ungrouped_file_ids]);

  const handleToggleSelect = useCallback((fileId: string) => {
    setSelectedFileIds((prev: string[]) =>
      prev.includes(fileId)
        ? prev.filter((id: string) => id !== fileId)
        : [...prev, fileId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFileIds(data.ungrouped_file_ids.slice());
  }, [data.ungrouped_file_ids]);

  const handleClearSelection = useCallback(() => {
    setSelectedFileIds([]);
  }, []);

  const removeFromSelection = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const removed = new Set<string>(ids);
    setSelectedFileIds((prev: string[]) =>
      prev.filter((id: string) => !removed.has(id)),
    );
  }, []);

  const handleCreateManualGroup = useCallback(
    (fileIds: string[]) => {
      if (fileIds.length === 0) return;
      onCreateManualGroup(fileIds);
      removeFromSelection(fileIds);
    },
    [onCreateManualGroup, removeFromSelection],
  );

  const handleBulkMoveFromUngrouped = useCallback(
    (fileIds: string[], targetGroupId: string) => {
      if (fileIds.length === 0) return;
      for (const fileId of fileIds) {
        onMoveFile(
          fileId,
          { kind: "ungrouped" },
          { kind: "group", group_id: targetGroupId },
        );
      }
      removeFromSelection(fileIds);
    },
    [onMoveFile, removeFromSelection],
  );

  // Per-file "Move →" picker inside an editable group card. Closes
  // over the source group's id so the reducer dispatch carries the
  // correct source location.
  const renderFileMovePicker = useCallback(
    (sourceGroupId: string, fileId: string) => {
      const options: SelectOptionItem<string>[] = [
        { value: UNGROUPED_TARGET_VALUE, label: "Ungrouped" },
      ];
      for (const g of editableGroups) {
        if (g.id === sourceGroupId) continue;
        options.push({
          value: g.id,
          label: labelForGroup(g, customerById),
        });
      }
      return (
        <FileMovePicker
          options={options}
          onMove={(value: string) => {
            const source: FileLocationRef = {
              kind: "group",
              group_id: sourceGroupId,
            };
            const target: FileLocationRef =
              value === UNGROUPED_TARGET_VALUE
                ? { kind: "ungrouped" }
                : { kind: "group", group_id: value };
            onMoveFile(fileId, source, target);
          }}
        />
      );
    },
    [editableGroups, customerById, onMoveFile],
  );

  const canContinue = blocking_errors.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Manual grouping</h2>
        <p className="text-sm text-muted-foreground">
          Assign every remaining PDF to a customer group. You can move
          files between groups, dissolve a group back to ungrouped, or
          create new groups from selections. Approved groups are
          locked — uncheck them earlier in the wizard if they need
          editing.
        </p>
      </div>

      <UngroupedPanel
        ungrouped_file_ids={data.ungrouped_file_ids}
        files={data.files}
        movable_target_groups={ungroupedMoveTargets}
        selected_file_ids={sanitizedSelectedFileIds}
        onToggleSelect={handleToggleSelect}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        onCreateManualGroup={handleCreateManualGroup}
        onMoveToGroup={handleBulkMoveFromUngrouped}
      />

      {editableGroups.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Editable groups
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {editableGroups.map((group: Group) => {
              const dupWarning = duplicateGroupIds.has(group.id)
                ? "This customer is selected in another group. Resolve the duplicate before continuing."
                : undefined;
              return (
                <GroupCard
                  key={group.id}
                  group={group}
                  files={data.files}
                  customers={customers}
                  showCustomerPicker
                  customerPickerError={dupWarning}
                  onCustomerChange={(customerId: string | null) =>
                    onSelectCustomer(group.id, customerId)
                  }
                  onDissolve={() => onDissolveGroup(group.id)}
                  renderFileActions={(fileId: string) =>
                    renderFileMovePicker(group.id, fileId)
                  }
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {approvedGroups.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Approved groups (locked)
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {approvedGroups.map((group: Group) => (
              <GroupCard
                key={group.id}
                group={group}
                files={data.files}
                customers={customers}
                showCustomerPicker={false}
                readOnly
              />
            ))}
          </div>
        </div>
      ) : null}

      {blocking_errors.length > 0 ? (
        <div className="rounded border border-muted bg-muted/30 p-2 text-xs text-muted-foreground flex flex-col gap-0.5">
          {blocking_errors.map((e: BlockingError, idx: number) => (
            <span key={`${e.kind}-${idx}`}>• {describeBlockingError(e)}</span>
          ))}
        </div>
      ) : null}

      <div className="flex flex-row justify-end">
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue to final review
        </Button>
      </div>
    </div>
  );
}

type FileMovePickerProps = {
  options: SelectOptionItem<string>[];
  onMove: (value: string) => void;
};

/**
 * Tiny per-file "Move →" picker rendered inside an editable group's
 * file list. Resets after each move so the same row can be moved
 * again to a different target.
 */
function FileMovePicker({ options, onMove }: FileMovePickerProps) {
  const [value, setValue] = useState<string>("");
  const disabled = options.length === 0;
  return (
    <div className="flex flex-row items-center gap-1">
      <span className="text-[10px] uppercase text-muted-foreground">
        Move →
      </span>
      <Combobox
        options={options}
        value={value}
        onValueChange={(v: string) => {
          if (v === "") return;
          onMove(v);
          setValue("");
        }}
        placeholder={disabled ? "No targets" : "Pick target..."}
        disabled={disabled}
        virtualizeList
        dwimOnChange
        isModal
        popoverClassName="md:min-w-[300px] lg:min-w-[400px]"
      />
    </div>
  );
}
